import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import {
  getTopupDetails,
  getWalletBalances,
  listCards,
  listCurrencies,
  type CryptoCurrency,
  type DepositDetails,
} from '../api/client.ts';
import { IconChevronLeft, IconCopy } from '../components/icons.tsx';
import { ErrorState, TopupLoadingSkeleton } from '../components/states.tsx';
import { showToast } from '../components/toast.tsx';
import { applyPendingSends } from '../lib/pending-sends.ts';
import { cardCurrencyToVault } from '../lib/vault-currency.ts';

import { rootRoute } from './__root.tsx';

export interface TopupSearch {
  readonly cardId?: string;
}

// The platform enforces a minimum top-up of 10 in the deposit currency. When
// the provider returns no per-currency minimum (or one below 10), floor it to
// 10 so the user is never told they can send less than the platform accepts.
// A backend minimum of 10 or more is shown verbatim to preserve its exact
// formatting.
const MIN_TOPUP = 10;
const flooredMinimum = (raw: string | undefined): string => {
  if (raw === undefined || raw === '') return String(MIN_TOPUP);
  const n = Number(raw);
  if (!Number.isFinite(n) || n < MIN_TOPUP) return String(MIN_TOPUP);
  return raw;
};

// Crypto addresses overflow a mobile line. Show a middle-ellipsis preview
// (head + tail, enough to eyeball-verify against the wallet) on screen, while
// copy and screen readers still get the full, unabridged address.
const truncateMiddle = (value: string, head = 10, tail = 8): string =>
  value.length > head + tail + 1 ? `${value.slice(0, head)}…${value.slice(-tail)}` : value;

// Reused 2D context for measuring text width. Null in non-DOM/test envs.
const measureCtx: CanvasRenderingContext2D | null =
  typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');

// Middle-truncates `address` to exactly the number of monospace characters
// that fit the element's current width, recomputing on resize. The font is
// JetBrains Mono so every glyph is the same width — measure the whole string
// once and divide. Falls back to a fixed head/tail when measurement is
// unavailable (no canvas, zero-width layout, jsdom) so the value is never
// shown unabridged on a narrow line.
const useFittedMiddle = (
  address: string,
): { readonly ref: RefObject<HTMLParagraphElement | null>; readonly display: string } => {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [display, setDisplay] = useState<string>(() => truncateMiddle(address));

  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null || measureCtx === null) {
      setDisplay(truncateMiddle(address));
      return;
    }
    const compute = (): void => {
      const avail = el.clientWidth;
      if (avail <= 0) {
        setDisplay(truncateMiddle(address));
        return;
      }
      const cs = window.getComputedStyle(el);
      measureCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      const letterSpacing = Number.parseFloat(cs.letterSpacing) || 0;
      const widthOf = (s: string): number =>
        measureCtx.measureText(s).width + letterSpacing * s.length;
      if (widthOf(address) <= avail) {
        setDisplay(address);
        return;
      }
      const perChar = widthOf(address) / address.length;
      if (perChar <= 0) {
        setDisplay(truncateMiddle(address));
        return;
      }
      // -1 leaves room for the ellipsis glyph itself.
      const maxChars = Math.max(7, Math.floor(avail / perChar) - 1);
      const head = Math.ceil(maxChars / 2);
      const tail = maxChars - head;
      setDisplay(`${address.slice(0, head)}…${address.slice(-tail)}`);
    };
    compute();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return (): void => {
      observer.disconnect();
    };
  }, [address]);

  return { ref, display };
};

const copyToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const Step1 = ({
  card,
  currencies,
  onPickCurrency,
  onBack,
  pending,
  pendingCurrencyId,
}: {
  readonly card: { readonly lastFour?: string; readonly cardLabel?: string } | undefined;
  readonly currencies: readonly CryptoCurrency[];
  readonly onPickCurrency: (id: string) => void;
  readonly onBack: () => void;
  readonly pending: boolean;
  readonly pendingCurrencyId: string;
}): ReactNode => (
  <main className="screen">
    <div className="topbar fade-in">
      <button type="button" className="topbar-back" aria-label="Back" onClick={onBack}>
        <IconChevronLeft />
      </button>
      <h1 className="topbar-title topbar-title--quiet">Top up</h1>
      <span className="topbar-spacer" aria-hidden="true" />
    </div>

    {card !== undefined ? (
      <div className="fade-in d2" style={{ marginBottom: 24 }}>
        {/* First section after the topbar — drop the inter-section top margin
            so the card sits tight under the header instead of floating in
            empty space. */}
        <div className="section-header" style={{ marginTop: 0 }}>
          <p className="eyebrow">To card</p>
        </div>
        <div className="hairline" style={{ marginTop: 8, marginBottom: 4 }} />
        {/* Read-only — top-up is locked to the card the user arrived from. */}
        <div className="topup-target-card" style={{ padding: '16px 0' }}>
          <p className="currency-name">•••• {card.lastFour ?? '????'}</p>
          <p className="currency-network">{card.cardLabel ?? 'Card'}</p>
        </div>
      </div>
    ) : null}

    <div className="hero fade-in d1" style={{ padding: '8px 0 24px' }}>
      <p className="eyebrow hero-eyebrow">Choose a currency</p>
      <p className="hero-meta" style={{ marginTop: 8, lineHeight: 1.5 }}>
        Pick the crypto you want to send. We&apos;ll show you the deposit address and the minimum
        amount to send.
      </p>
    </div>

    <div className="section-header fade-in d2">
      <p className="eyebrow">Available currencies</p>
      <p className="status-line">
        {currencies.length === 0
          ? ''
          : `${String(currencies.length)} ${currencies.length === 1 ? 'currency' : 'currencies'}`}
      </p>
    </div>
    <div className="hairline fade-in d2" style={{ marginTop: 8 }} />

    <div className="fade-in d3">
      {currencies.length === 0 ? (
        <div className="empty-state">No currencies available</div>
      ) : (
        currencies.map((c, i) => {
          const isLoadingThis = pending && pendingCurrencyId === c.id;
          return (
            <div key={c.id}>
              <button
                type="button"
                className={`currency-row${isLoadingThis ? ' currency-row--loading' : ''}`}
                onClick={(): void => {
                  onPickCurrency(c.id);
                }}
                disabled={pending}
                aria-busy={isLoadingThis}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="currency-name">{c.symbol}</p>
                  <p className="currency-network">
                    {c.name} · {c.network}
                  </p>
                </div>
                {isLoadingThis ? (
                  <span className="currency-row-spinner" aria-hidden="true" />
                ) : (
                  <>
                    <div style={{ textAlign: 'right' }}>
                      <p className="currency-min">
                        Min {flooredMinimum(c.minimumAmount)} {c.symbol}
                      </p>
                    </div>
                    <div
                      className="currency-chevron"
                      style={{
                        marginLeft: 12,
                        color: 'var(--text-secondary)',
                        fontSize: 20,
                        lineHeight: 1,
                      }}
                      aria-hidden="true"
                    >
                      ›
                    </div>
                  </>
                )}
              </button>
              {i < currencies.length - 1 ? <div className="hairline" /> : null}
            </div>
          );
        })
      )}
    </div>
  </main>
);

const Step2 = ({
  currency,
  details,
  onBack,
  onDone,
  onTopUpFromVault,
}: {
  readonly currency: CryptoCurrency;
  readonly details: DepositDetails;
  readonly onBack: () => void;
  readonly onDone: () => void;
  // Present only when the selected currency is held in the user's Vault; renders
  // the "Top up from Vault" shortcut as the primary action.
  readonly onTopUpFromVault?: () => void;
}): ReactNode => {
  const onCopy = (): void => {
    void copyToClipboard(details.address).then((ok) => {
      showToast(ok ? 'Address copied' : 'Copy failed');
    });
  };

  const minLine = `Send at least ${flooredMinimum(details.minimumAmount)} ${currency.symbol}`;
  const fittedAddress = useFittedMiddle(details.address);

  return (
    <main className="screen">
      <div className="topbar">
        <button type="button" className="topbar-back" aria-label="Back" onClick={onBack}>
          <IconChevronLeft />
        </button>
        <h1 className="topbar-title topbar-title--quiet">Deposit</h1>
        <span className="topbar-spacer" aria-hidden="true" />
      </div>

      <div className="topup-deposit-summary fade-in d1">
        <p className="eyebrow hero-eyebrow">Send {currency.symbol}</p>
        <p className="topup-deposit-network">
          {currency.name} · {currency.network}
        </p>
        <p className="topup-deposit-min">{minLine}</p>
      </div>

      <p className="eyebrow" style={{ marginBottom: 18 }}>
        Deposit address
      </p>

      <div className="qr-frame">
        {details.qrCode !== undefined && details.qrCode !== '' ? (
          <img src={details.qrCode} alt="Deposit QR code" />
        ) : (
          <div className="qr-frame__empty">QR unavailable</div>
        )}
      </div>

      <div className="address-frame">
        <div className="address-row">
          <p className="address-text" title={details.address} ref={fittedAddress.ref}>
            <span aria-hidden="true">{fittedAddress.display}</span>
            <span className="sr-only">{details.address}</span>
          </p>
          <button type="button" className="address-copy" aria-label="Copy address" onClick={onCopy}>
            <IconCopy />
          </button>
        </div>
      </div>

      <p className="topup-deposit-hint" role="note">
        We&apos;ll notify you when funds arrive.
      </p>

      {onTopUpFromVault !== undefined ? (
        <>
          <button type="button" className="btn-primary" onClick={onTopUpFromVault}>
            Top up from Vault
          </button>
          <div style={{ height: 12 }} />
          <button type="button" className="btn-tertiary" onClick={onDone}>
            Done
          </button>
        </>
      ) : (
        <>
          <button type="button" className="btn-primary" onClick={onDone}>
            Done
          </button>
          <div style={{ height: 12 }} />
          <button type="button" className="btn-tertiary" onClick={onBack}>
            Choose another currency
          </button>
        </>
      )}
    </main>
  );
};

export const TopupPage = (): ReactNode => {
  const navigate = useNavigate();
  const search = topupRoute.useSearch();
  const [cardId, setCardId] = useState<string>(search.cardId ?? '');
  const [pickedCurrencyId, setPickedCurrencyId] = useState<string>('');
  const [pendingCurrencyId, setPendingCurrencyId] = useState<string>('');
  const [step, setStep] = useState<1 | 2>(1);

  const cardsQ = useQuery({ queryKey: ['cards'], queryFn: ({ signal }) => listCards(signal) });
  const currenciesQ = useQuery({
    queryKey: ['currencies'],
    queryFn: ({ signal }) => listCurrencies(signal),
  });
  // Drives the "Top up from Vault" shortcut on the deposit screen. Failure is
  // non-fatal — the shortcut simply stays hidden.
  const balancesQ = useQuery({
    queryKey: ['wallet-balances'],
    queryFn: ({ signal }) => getWalletBalances(signal),
    staleTime: 30_000,
    select: applyPendingSends,
  });

  useEffect(() => {
    if (cardId === '') {
      const first = cardsQ.data?.cards[0]?.id;
      if (first !== undefined) setCardId(first);
    }
  }, [cardId, cardsQ.data?.cards]);

  const detailsM = useMutation({
    mutationFn: ({ cardId: c, currencyId: cur }: { cardId: string; currencyId: string }) =>
      getTopupDetails(c, cur),
    onSuccess: () => {
      setStep(2);
    },
    onSettled: () => {
      setPendingCurrencyId('');
    },
  });

  if (cardsQ.isLoading || currenciesQ.isLoading) {
    return <TopupLoadingSkeleton />;
  }
  if (cardsQ.isError || currenciesQ.isError) {
    return (
      <ErrorState
        message="Could not load top-up data"
        onRetry={(): void => {
          void cardsQ.refetch();
          void currenciesQ.refetch();
        }}
      />
    );
  }

  const currencies = currenciesQ.data?.currencies ?? [];
  const selectedCard = cardsQ.data?.cards.find((c) => c.id === cardId);
  const pickedCurrency = currencies.find((c) => c.id === pickedCurrencyId);

  if (step === 2 && detailsM.data !== undefined && pickedCurrency !== undefined) {
    // Offer the Vault shortcut only when the picked currency maps to a vault
    // currency the user actually holds a wallet for.
    const vaultCcy = cardCurrencyToVault(pickedCurrency.symbol, pickedCurrency.network);
    const vaultHasCurrency =
      vaultCcy !== null &&
      (balancesQ.data?.balances.some((b) => b.currency === vaultCcy && b.hasAddress === true) ??
        false);
    // Spread the optional prop only when eligible — exactOptionalPropertyTypes
    // forbids passing an explicit `undefined`.
    const vaultShortcut = vaultHasCurrency
      ? {
          onTopUpFromVault: (): void => {
            void navigate({
              to: '/topup/from-vault',
              search: { cardId, currencyId: pickedCurrencyId },
            });
          },
        }
      : {};
    return (
      <Step2
        currency={pickedCurrency}
        details={detailsM.data}
        onBack={(): void => {
          setStep(1);
          detailsM.reset();
        }}
        onDone={(): void => {
          showToast('We will notify you when the deposit is received');
          window.setTimeout(() => {
            void navigate({ to: '/' });
          }, 800);
        }}
        {...vaultShortcut}
      />
    );
  }

  return (
    <>
      <Step1
        card={selectedCard}
        currencies={currencies}
        onPickCurrency={(id): void => {
          if (cardId === '') return;
          setPickedCurrencyId(id);
          setPendingCurrencyId(id);
          detailsM.mutate({ cardId, currencyId: id });
        }}
        onBack={(): void => {
          void navigate({ to: '/' });
        }}
        pending={detailsM.isPending}
        pendingCurrencyId={pendingCurrencyId}
      />
      {detailsM.isError ? (
        <ErrorState
          message={
            detailsM.error instanceof Error
              ? detailsM.error.message
              : 'Failed to generate deposit address'
          }
        />
      ) : null}
    </>
  );
};

export const topupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/topup',
  component: TopupPage,
  validateSearch: (s: Record<string, unknown>): TopupSearch =>
    typeof s.cardId === 'string' ? { cardId: s.cardId } : {},
});
